import type { CallRecordListItem } from '@yeshuman/sdk'
import {
  endOfDay,
  isValid,
  isWithinInterval,
  parseISO,
  startOfDay,
} from 'date-fns'

import type { CallOutcome } from '../hooks/use-call-outcomes'

const ENDED_REASON_TOP = 8

function inLocalCalendarDay(iso: string | null, dayStart: Date, dayEnd: Date): boolean {
  if (!iso) return false
  const d = parseISO(iso)
  if (!isValid(d)) return false
  return isWithinInterval(d, { start: dayStart, end: dayEnd })
}

function median(sorted: number[]): number | null {
  if (sorted.length === 0) return null
  const mid = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 1) return sorted[mid]
  return (sorted[mid - 1] + sorted[mid]) / 2
}

function normalizeCallState(state: string | undefined): 'emergency' | 'elevated' | 'routine' {
  const s = (state || 'routine').toLowerCase()
  if (s === 'emergency') return 'emergency'
  if (s === 'elevated') return 'elevated'
  return 'routine'
}

/** Completed calls whose evaluation is not finished (and not failed). */
export function isEvaluationBacklog(row: CallRecordListItem): boolean {
  return (
    row.is_complete &&
    row.evaluation_status !== 'completed' &&
    row.evaluation_status !== 'evaluation_failed'
  )
}

export interface NamedCount {
  name: string
  count: number
}

export interface UrgencyCounts {
  emergency: number
  elevated: number
  routine: number
}

export interface CallOperatorCallMetrics {
  activeCount: number
  completedToday: number
  completedInWindow: number
  avgDurationSeconds: number | null
  medianDurationSeconds: number | null
  durationHistogram: NamedCount[]
  callStatusSeries: NamedCount[]
  endedReasonSeries: NamedCount[]
  evaluationStatusSeries: NamedCount[]
  evaluationsCompleteCount: number
  /** Percent of completed calls in window with evaluation_status === completed */
  evaluationsCompletePct: number | null
  urgencyCounts: UrgencyCounts
  evaluationFailedCount: number
  evaluationBacklogCount: number
}

export function buildCallOperatorCallMetrics(
  records: CallRecordListItem[]
): CallOperatorCallMetrics {
  const now = new Date()
  const dayStart = startOfDay(now)
  const dayEnd = endOfDay(now)

  let activeCount = 0
  let completedToday = 0
  let completedInWindow = 0
  let evaluationsCompleteCount = 0
  let evaluationFailedCount = 0
  let evaluationBacklogCount = 0
  const urgencyCounts: UrgencyCounts = {
    emergency: 0,
    elevated: 0,
    routine: 0,
  }
  const durations: number[] = []
  const durationBuckets = {
    'Under 1m': 0,
    '1–3m': 0,
    '3–5m': 0,
    '5m+': 0,
  } as Record<string, number>
  const statusMap = new Map<string, number>()
  const endedMap = new Map<string, number>()
  const evalMap = new Map<string, number>()

  for (const r of records) {
    urgencyCounts[normalizeCallState(r.call_state)] += 1

    if (!r.is_complete) {
      activeCount += 1
    } else {
      completedInWindow += 1
      if (r.evaluation_status === 'completed') {
        evaluationsCompleteCount += 1
      }
      if (r.evaluation_status === 'evaluation_failed') {
        evaluationFailedCount += 1
      }
      if (isEvaluationBacklog(r)) {
        evaluationBacklogCount += 1
      }
      const endRef = r.ended_at ?? r.created_at
      if (
        inLocalCalendarDay(endRef, dayStart, dayEnd) ||
        inLocalCalendarDay(r.created_at, dayStart, dayEnd)
      ) {
        completedToday += 1
      }
    }

    const ds = r.duration_seconds
    if (r.is_complete && ds != null && ds >= 0) {
      durations.push(ds)
      if (ds < 60) durationBuckets['Under 1m'] += 1
      else if (ds < 180) durationBuckets['1–3m'] += 1
      else if (ds < 300) durationBuckets['3–5m'] += 1
      else durationBuckets['5m+'] += 1
    }

    const st = (r.call_status || '').trim() || '(none)'
    statusMap.set(st, (statusMap.get(st) ?? 0) + 1)

    const er = (r.ended_reason || '').trim() || '(none)'
    endedMap.set(er, (endedMap.get(er) ?? 0) + 1)

    const ev = (r.evaluation_status || '').trim() || '(none)'
    evalMap.set(ev, (evalMap.get(ev) ?? 0) + 1)
  }

  durations.sort((a, b) => a - b)
  const avgDurationSeconds =
    durations.length > 0
      ? Math.round(durations.reduce((s, x) => s + x, 0) / durations.length)
      : null
  const medianDurationSeconds = median(durations)

  const endedSorted = [...endedMap.entries()].sort((a, b) => b[1] - a[1])
  const endedTop = endedSorted.slice(0, ENDED_REASON_TOP)
  const endedRest = endedSorted.slice(ENDED_REASON_TOP).reduce((s, [, c]) => s + c, 0)
  const endedReasonSeries: NamedCount[] = endedTop.map(([name, count]) => ({
    name,
    count,
  }))
  if (endedRest > 0) {
    endedReasonSeries.push({ name: 'Other', count: endedRest })
  }

  const durationHistogram: NamedCount[] = Object.entries(durationBuckets).map(
    ([name, count]) => ({ name, count })
  )

  const toSeries = (m: Map<string, number>): NamedCount[] =>
    [...m.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }))

  return {
    activeCount,
    completedToday,
    completedInWindow,
    avgDurationSeconds,
    medianDurationSeconds,
    durationHistogram,
    callStatusSeries: toSeries(statusMap),
    endedReasonSeries,
    evaluationStatusSeries: toSeries(evalMap),
    evaluationsCompleteCount,
    evaluationsCompletePct:
      completedInWindow > 0
        ? Math.round((100 * evaluationsCompleteCount) / completedInWindow)
        : null,
    urgencyCounts,
    evaluationFailedCount,
    evaluationBacklogCount,
  }
}

/**
 * Urgent live calls first (emergency → elevated), then other live, capped.
 * Used by the Needs attention module on the operator home.
 */
export function selectAttentionCalls(
  records: CallRecordListItem[],
  limit = 4
): CallRecordListItem[] {
  const urgencyRank = (state: string | undefined) => {
    const s = normalizeCallState(state)
    if (s === 'emergency') return 0
    if (s === 'elevated') return 1
    return 2
  }

  const liveUrgent = records
    .filter((r) => {
      if (r.is_complete) return false
      const s = normalizeCallState(r.call_state)
      return s === 'emergency' || s === 'elevated'
    })
    .sort((a, b) => {
      const ur = urgencyRank(a.call_state) - urgencyRank(b.call_state)
      if (ur !== 0) return ur
      return (b.updated_at || '').localeCompare(a.updated_at || '')
    })

  if (liveUrgent.length >= limit) {
    return liveUrgent.slice(0, limit)
  }

  const remaining = limit - liveUrgent.length
  const liveRoutine = records
    .filter((r) => !r.is_complete && normalizeCallState(r.call_state) === 'routine')
    .sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))
    .slice(0, remaining)

  return [...liveUrgent, ...liveRoutine]
}

export function latestOutcomePerCall(outcomes: CallOutcome[]): Map<string, CallOutcome> {
  const map = new Map<string, CallOutcome>()
  for (const o of outcomes) {
    const prev = map.get(o.call_record_id)
    if (!prev || o.updated_at > prev.updated_at) {
      map.set(o.call_record_id, o)
    }
  }
  return map
}

export interface CallOperatorOutcomeMetrics {
  bookingsMade: number
  /** Latest outcome is booking_requested but not booked/completed */
  fallOffBookingRequested: number
  /** Latest outcome_status === cancelled */
  fallOffCancelled: number
  /** Count of latest outcomes per status */
  outcomeStatusSeries: NamedCount[]
  /** Completed calls in records with no outcome row in window */
  completedCallsNoOutcome: number
  /** Completed calls whose latest outcome is only open */
  completedCallsOutcomeOpenOnly: number
  /** bookingsMade / completedInWindow as percent, or null when no completed calls */
  bookingConversionPct: number | null
}

function isBookingSuccess(o: CallOutcome): boolean {
  if (o.outcome_status === 'booked' || o.outcome_status === 'completed') {
    return true
  }
  const b = o.latest_booking
  if (b && b.booking_status === 'confirmed') {
    return true
  }
  return false
}

export function buildCallOperatorOutcomeMetrics(
  records: CallRecordListItem[],
  outcomes: CallOutcome[]
): CallOperatorOutcomeMetrics {
  const latest = latestOutcomePerCall(outcomes)
  const statusCounts = new Map<string, number>()

  let bookingsMade = 0
  let fallOffBookingRequested = 0
  let fallOffCancelled = 0

  for (const o of latest.values()) {
    statusCounts.set(
      o.outcome_status,
      (statusCounts.get(o.outcome_status) ?? 0) + 1
    )
    if (isBookingSuccess(o)) {
      bookingsMade += 1
    }
    if (o.outcome_status === 'cancelled') {
      fallOffCancelled += 1
    }
    if (
      o.outcome_status === 'booking_requested' &&
      !isBookingSuccess(o)
    ) {
      fallOffBookingRequested += 1
    }
  }

  let completedCallsNoOutcome = 0
  let completedCallsOutcomeOpenOnly = 0
  let completedInWindow = 0
  for (const r of records) {
    if (!r.is_complete) continue
    completedInWindow += 1
    const o = latest.get(r.id)
    if (!o) {
      completedCallsNoOutcome += 1
    } else if (o.outcome_status === 'open') {
      completedCallsOutcomeOpenOnly += 1
    }
  }

  const outcomeStatusSeries: NamedCount[] = [...statusCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([name, count]) => ({ name, count }))

  return {
    bookingsMade,
    fallOffBookingRequested,
    fallOffCancelled,
    outcomeStatusSeries,
    completedCallsNoOutcome,
    completedCallsOutcomeOpenOnly,
    bookingConversionPct:
      completedInWindow > 0
        ? Math.round((100 * bookingsMade) / completedInWindow)
        : null,
  }
}
